import { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft,
    Play,
    CheckCircle2,
    Send,
    Trophy,
    XCircle,
    Sparkles,
    ArrowRight,
    Lock,
    BookOpen,
    Presentation,
    AlignLeft,
    Headphones,
} from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import CommentThread from "../components/CommentThread";
import RichLesson from "../components/RichLesson";
import LessonPresentation from "../components/LessonPresentation";
import { usePodcast } from "../lib/PodcastContext";
import useLearningHeartbeat from "../hooks/useLearningHeartbeat";

function getEmbedUrl(url) {
    if (!url) return null;
    if (url.includes("youtube.com/embed")) return url;
    const match = url.match(/(?:youtube\.com.*v=|youtu\.be\/)([^&?]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return url;
}

// Iter-27.30 — French level labels (niveau in sidebar + subtitle)
const LEVEL_FR = {
    beginner: "Débutant",
    intermediate: "Intermédiaire",
    advanced: "Avancé",
    expert: "Expert",
};

function Quiz({ quiz, onSubmit, lastResult, onboardingMode, nextActionSlot }) {
    const navigate = useNavigate();
    const [answers, setAnswers] = useState(Array(quiz.questions.length).fill(-1));
    const [submitting, setSubmitting] = useState(false);
    const allAnswered = answers.every((a) => a >= 0);

    const submit = async () => {
        setSubmitting(true);
        try {
            await onSubmit(answers);
        } finally {
            setSubmitting(false);
        }
    };

    if (lastResult) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
                data-testid="quiz-result"
            >
                <div
                    className={`rounded-3xl p-6 text-center ${
                        lastResult.passed ? "bg-shariti-orange" : "bg-shariti-surface"
                    }`}
                >
                    <Trophy className="w-12 h-12 mx-auto mb-3 text-white" />
                    <p className="text-5xl font-extrabold text-white mb-1">
                        {lastResult.score}%
                    </p>
                    <p className="text-white/90 font-bold">
                        {lastResult.passed
                            ? `Validé ! +${lastResult.points_earned || 0} points`
                            : "Pas encore validé — repasse le quiz"}
                    </p>
                </div>

                {/* Onboarding-specific CTA: when quiz is passed and we came from onboarding,
                    surface a single, very visible button to go back and finalize the onboarding. */}
                {onboardingMode && lastResult.passed && (
                    <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        onClick={() => navigate("/onboarding")}
                        data-testid="quiz-back-to-onboarding"
                        className="w-full bg-shariti-orange text-white font-extrabold py-4 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all flex items-center justify-center gap-2"
                    >
                        <CheckCircle2 className="w-5 h-5" />
                        Retourner à l&apos;onboarding pour débloquer la plateforme
                        <ArrowRight className="w-5 h-5" />
                    </motion.button>
                )}

                {/* Iter-30.57 — Slot pour CTA "Module suivant" / "Bravo terminé"
                    injecté par le parent, placé IMMÉDIATEMENT sous le badge
                    de score pour qu'il soit visible sans scroller la liste
                    de feedback (10+ questions sur mobile = très long). */}
                {!onboardingMode && lastResult.passed && nextActionSlot}

                <div className="space-y-3">
                    {lastResult.feedback.map((f, i) => (
                        <div
                            key={i}
                            className="glass-card rounded-2xl p-4 flex gap-3"
                        >
                            {f.is_correct ? (
                                <CheckCircle2 className="w-5 h-5 text-shariti-orange shrink-0 mt-0.5" />
                            ) : (
                                <XCircle className="w-5 h-5 text-shariti-orange-light shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-white mb-1">{f.q}</p>
                                <p className="text-xs text-white/60">{f.explanation}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        );
    }

    return (
        <div className="space-y-6" data-testid="quiz-form">
            {quiz.questions.map((q, i) => (
                <div key={i} className="glass-card rounded-3xl p-5">
                    <p className="text-xs font-thin tracking-[0.25em] text-shariti-orange-light uppercase mb-2">
                        Question {i + 1}/{quiz.questions.length}
                    </p>
                    <p className="text-lg font-extrabold text-white mb-4 tracking-tight">
                        {q.q}
                    </p>
                    <div className="space-y-2">
                        {q.options.map((opt, j) => (
                            <button
                                key={j}
                                onClick={() => {
                                    const newA = [...answers];
                                    newA[i] = j;
                                    setAnswers(newA);
                                }}
                                data-testid={`quiz-q${i}-opt${j}`}
                                className={`w-full text-left p-3 rounded-2xl border transition-all font-medium ${
                                    answers[i] === j
                                        ? "bg-shariti-orange border-shariti-orange text-white"
                                        : "bg-white/3 border-white/5 text-white/70 hover:border-shariti-orange/40"
                                }`}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
            <button
                onClick={submit}
                disabled={!allAnswered || submitting}
                data-testid="quiz-submit-button"
                className="w-full bg-shariti-orange text-white font-extrabold py-4 rounded-2xl orange-glow disabled:opacity-50 hover:bg-shariti-orange-light transition-all"
            >
                {submitting ? "Validation..." : "Valider mes réponses"}
            </button>
        </div>
    );
}

export default function Module() {
    const { id } = useParams();
    const { user } = useAuth();
    const podcast = usePodcast();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = ["quiz", "comments"].includes(searchParams.get("tab"))
        ? searchParams.get("tab")
        : "lessons";
    const onboardingMode = searchParams.get("onboarding") === "1";
    const [data, setData] = useState(null);
    const [activeLessonIdx, setActiveLessonIdx] = useState(0);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [tab, setTab] = useState(initialTab); // lessons | quiz | comments
    const [quizResult, setQuizResult] = useState(null);
    const quizSectionRef = useRef(null);
    const lessonTopRef = useRef(null);
    // Iter-27.28 — Anti-farming: track elapsed seconds since lesson opened.
    const [waitRemaining, setWaitRemaining] = useState(0);
    const [minSeconds, setMinSeconds] = useState(0);
    const lessonStartedAtRef = useRef(null);
    // Iter-27.32 — Hardened anti-farming :
    //  • Pause countdown when document is hidden (Page Visibility API)
    //  • Require user to scroll to bottom of lesson content (Intersection Observer)
    const [pageVisible, setPageVisible] = useState(true);
    const [scrolledToEnd, setScrolledToEnd] = useState(false);
    const lessonEndSentinelRef = useRef(null);
    // Iter-27.36 / Iter-30.31 — Mode Présentation Premium par défaut.
    // Persisté en localStorage par module pour respecter le choix utilisateur.
    //
    // Iter-30.31 — Sur mobile (<640 px), on FORCE Présentation au premier load
    // de chaque arrivée sur le module, sans tenir compte du localStorage. La
    // vue Classique (texte long en scroll) est très peu adaptée à un écran
    // smartphone, et surtout le bouton "Écouter" / mode podcast n'y est pas
    // visible → l'apprenant ne sait pas qu'il peut écouter. Le toggle reste
    // disponible pour basculer manuellement durant la session.
    const presentationModeKey = `shariti_pres_mode_${id}`;
    const isMobileViewport = () =>
        typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
    const [presentationMode, setPresentationMode] = useState(() => {
        if (isMobileViewport()) return true;
        const saved = localStorage.getItem(presentationModeKey);
        return saved === null ? true : saved === "1";
    });
    const [allSlidesViewed, setAllSlidesViewed] = useState(false);
    useEffect(() => {
        // On ne persiste le choix QUE sur desktop. Sur mobile, on veut
        // toujours rouvrir un module en mode Présentation par défaut.
        if (!isMobileViewport()) {
            localStorage.setItem(presentationModeKey, presentationMode ? "1" : "0");
        }
    }, [presentationMode, presentationModeKey]);

    // Iter-30.43 — Sur mobile, on remet la leçon active en mode Présentation
    // à chaque changement de leçon (Leçon suivante, clic dans la sidebar des
    // leçons, enchaînement podcast, etc.). Sinon, un apprenant mobile qui
    // avait basculé en Classique pour la leçon 1 verrait la leçon 2 démarrer
    // aussi en Classique — alors qu'à chaque nouvelle leçon il s'attend à
    // l'expérience immersive (slides + bouton Écouter) qui est l'UX cible
    // sur smartphone. Pas de régression desktop (le check `isMobileViewport()`
    // y retourne false → no-op).
    useEffect(() => {
        if (isMobileViewport()) {
            setPresentationMode(true);
        }
    }, [activeLessonIdx]);

    useEffect(() => {
        (async () => {
            const r = await api.get(`/modules/${id}`);
            setData(r.data);
            const c = await api.get(`/comments/${id}`);
            setComments(c.data);
        })();
    }, [id]);

    // Iter-30.54 — Rafraîchir la progression quand le podcast valide une leçon
    // ou que le podcast se termine sur ce module. Sans ça, le user voit toujours
    // l'ancienne progression (50%) jusqu'au reload manuel et le module suivant
    // reste verrouillé visuellement.
    useEffect(() => {
        const handler = async (evt) => {
            if (evt.detail?.moduleId !== id) return;
            try {
                const r = await api.get(`/modules/${id}`);
                setData(r.data);
            } catch { /* */ }
        };
        window.addEventListener("shariti:podcast-lesson-completed", handler);
        window.addEventListener("shariti:podcast-finished", handler);
        return () => {
            window.removeEventListener("shariti:podcast-lesson-completed", handler);
            window.removeEventListener("shariti:podcast-finished", handler);
        };
    }, [id]);

    // Iter-27.96 — Quand l'utilisateur navigue d'un module à l'autre via le CTA
    // "Continuer avec : [module suivant]" (Iter-27.86), React Router met juste
    // l'URL à jour mais ne remonte PAS le composant Module. Résultat : `tab`,
    // `activeLessonIdx`, `quizResult` etc. gardent l'état du module précédent
    // → l'apprenant atterrit sur l'onglet Quiz du module B au lieu de la 1ère
    // leçon. On reset explicitement tout le state utilisateur au changement
    // d'id pour rejouer le flow "depuis le début" sur le nouveau module.
    //
    // Iter-27.97 — En plus du reset, le CTA "Continuer avec" propage un query
    // param `?present=1` qui FORCE le mode présentation, même si la préférence
    // localStorage du module suivant était "vue classique". Le choix utilisateur
    // n'est pas perdu — il est restauré dès qu'il navigue manuellement vers ce
    // même module via le parcours.
    useEffect(() => {
        const fresh = !["quiz", "comments"].includes(searchParams.get("tab"))
            ? "lessons"
            : searchParams.get("tab");
        setTab(fresh);
        setActiveLessonIdx(0);
        setQuizResult(null);
        setAllSlidesViewed(false);
        setScrolledToEnd(false);
        setWaitRemaining(0);
        setMinSeconds(0);
        // Mode présentation :
        // 1) Si ?present=1 → force présentation (arrivée via CTA "Continuer avec"
        //    ou depuis l'onboarding — Iter-30.31)
        // 2) Sinon → respecte la préférence localStorage du module
        //    (uniquement sur desktop — sur mobile on force Présentation)
        // 3) Sinon (jamais visité) → présentation par défaut
        if (searchParams.get("present") === "1") {
            setPresentationMode(true);
            localStorage.setItem(`shariti_pres_mode_${id}`, "1");
            // On nettoie le query param de l'URL pour pas qu'il persiste
            // (l'user verra une URL propre /module/xxx).
            const next = new URLSearchParams(searchParams);
            next.delete("present");
            setSearchParams(next, { replace: true });
        } else if (isMobileViewport()) {
            // Iter-30.31 — Sur mobile, toujours Présentation par défaut.
            setPresentationMode(true);
        } else {
            const saved = localStorage.getItem(`shariti_pres_mode_${id}`);
            setPresentationMode(saved === null ? true : saved === "1");
        }
        // Scroll en haut de page au passage à un nouveau module pour que
        // l'apprenant voie bien le titre + l'intro avant de plonger.
        window.scrollTo({ top: 0, behavior: "smooth" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Iter-27.86 — Récupère le module suivant dans l'ordre du parcours, pour
    // proposer un CTA "Continuer avec le module suivant" après quiz validé.
    //
    // Iter-30.44 — Filtrage par audience : un manager qui finit un module
    // manager doit voir le PROCHAIN module manager, pas un module recruiter
    // avec un order similaire. Idem pour un recruiter sur module recruiter.
    // Sans ce filtre, le tri par order ASC retournait parfois un module de
    // l'autre catalogue (les 2 catalogues numérotent depuis 1), et le user
    // mobile était obligé de revenir sur /parcours pour trouver son vrai
    // prochain module.
    const [nextModule, setNextModule] = useState(null);
    useEffect(() => {
        if (!data?.module?.order && data?.module?.order !== 0) return;
        (async () => {
            try {
                const r = await api.get(`/modules`);
                const all = Array.isArray(r.data) ? r.data : [];
                const currentOrder = data.module.order;
                const currentAudience = data.module.audience || "recruiter";
                const candidates = all
                    .filter((m) => m.module_id !== id)
                    .filter((m) => m.is_hidden !== true)
                    .filter((m) => (m.audience || "recruiter") === currentAudience)
                    .filter((m) => typeof m.order === "number" && m.order > currentOrder)
                    .sort((a, b) => a.order - b.order);
                setNextModule(candidates[0] || null);
            } catch {
                setNextModule(null);
            }
        })();
    }, [id, data?.module?.order, data?.module?.audience]);

    // Iter-30.44 + 30.57 — Auto-scroll vers le CTA en haut du quiz dès qu'il
    // devient visible après validation. Couvre les 2 cas : nextModule exists
    // (CTA "Continuer avec") ET pas de next (CTA "Bravo terminé / Voir mon
    // parcours"). Sans ce scroll, le user mobile devait scroller manuellement
    // au-delà de la liste de feedback pour voir le CTA.
    // Note : ce ref + useEffect DOIVENT être avant l'early-return `if (!data)`
    // (Rules of Hooks).
    const nextModuleRef = useRef(null);
    useEffect(() => {
        if (quizResult?.passed && nextModuleRef.current) {
            const t = setTimeout(() => {
                nextModuleRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 450);
            return () => clearTimeout(t);
        }
    }, [quizResult?.passed, nextModule]);

    // When ?tab=quiz: smooth-scroll to the quiz section once data is loaded
    useEffect(() => {
        if (tab === "quiz" && data && quizSectionRef.current) {
            setTimeout(() => {
                quizSectionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }, 150);
        }
    }, [tab, data]);

    // Iter-27.28 — Auto-scroll to top of lesson when active lesson changes.
    // Also call /lessons/{id}/start to record when reading actually begins
    // (server-side anti-farming gate). Reset countdown timer.
    // Iter-27.32 — Also reset scrolledToEnd flag (must scroll to bottom each time).
    useEffect(() => {
        if (!data || tab !== "lessons") return;
        const active = data.lessons?.[activeLessonIdx];
        if (!active) return;
        setScrolledToEnd(false);
        setAllSlidesViewed(false);
        // Smooth scroll to lesson title (or top of page on mobile)
        setTimeout(() => {
            if (lessonTopRef.current) {
                lessonTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        }, 80);
        // Server-side start tracking
        let alive = true;
        (async () => {
            try {
                const r = await api.post(`/modules/${id}/lessons/${active.lesson_id}/start`);
                if (!alive) return;
                lessonStartedAtRef.current = Date.now();
                setMinSeconds(r.data.min_seconds || 0);
                setWaitRemaining(r.data.wait_seconds_remaining || 0);
            } catch {
                // Silently ignore — server will gate on /complete anyway.
            }
        })();
        return () => {
            alive = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLessonIdx, data?.module?.module_id, tab]);

    // Iter-27.32 — Page Visibility API: pause countdown when tab is hidden.
    useEffect(() => {
        const onVisChange = () => setPageVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVisChange);
        return () => document.removeEventListener("visibilitychange", onVisChange);
    }, []);

    // Iter-27.32 — Intersection Observer: detect when user scrolled to bottom of lesson.
    useEffect(() => {
        const sentinel = lessonEndSentinelRef.current;
        if (!sentinel) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) setScrolledToEnd(true);
            },
            { threshold: 0.1 },
        );
        obs.observe(sentinel);
        return () => obs.disconnect();
    // Re-bind whenever the active lesson changes (sentinel re-renders).
    }, [activeLessonIdx, tab, data?.module?.module_id]);

    // Iter-27.28 — Tick countdown every second when waitRemaining > 0.
    // Iter-27.32 — pause tick when document hidden.
    useEffect(() => {
        if (waitRemaining <= 0) return;
        if (!pageVisible) return;
        const t = setInterval(() => {
            setWaitRemaining((w) => Math.max(0, w - 1));
        }, 1000);
        return () => clearInterval(t);
    }, [waitRemaining, pageVisible]);

    const switchTab = (next) => {
        setTab(next);
        const sp = new URLSearchParams(searchParams);
        if (next === "lessons") sp.delete("tab");
        else sp.set("tab", next);
        setSearchParams(sp, { replace: true });
    };

    // Iter-30 — Tracking heartbeat des heures réelles d'apprentissage.
    // MUST be called unconditionally (before any early return) to obey Rules of Hooks.
    const _activeLessonForHeartbeat =
        (data?.lessons && data.lessons[activeLessonIdx]) ||
        (data?.lessons && data.lessons[0]);
    useLearningHeartbeat({
        moduleId: id,
        lessonId: _activeLessonForHeartbeat?.lesson_id,
    });

    if (!data) {
        // Iter-30 — Tracking heartbeat des heures réelles d'apprentissage
        // (hook still called below the early-return guard via separate effect chain).
        // To keep hook order stable across renders we ALWAYS call the hook BEFORE
        // any early return — see block above (moved before this guard).
        return (
            <Layout title="Chargement..." subtitle="">
                <div className="h-64 glass-card rounded-3xl animate-pulse" />
            </Layout>
        );
    }

    const { module: mod, lessons, quiz, progress } = data;
    const activeLesson = lessons[activeLessonIdx] || lessons[0];

    const allLessonsDone =
        lessons.length > 0 &&
        progress?.completed_lessons?.length === lessons.length;
    const quizPassed = (progress?.best_score ?? -1) >= 70;
    const moduleFullyCompleted = !!progress?.completed;

    const completeLesson = async (lessonId) => {
        try {
            await api.post(`/modules/${id}/lessons/${lessonId}/complete`);
            const r = await api.get(`/modules/${id}`);
            setData(r.data);
            // Iter-27.94 — Flow de fin de module fluide et intuitif :
            //   1. Pas la dernière leçon → leçon suivante (auto-advance) + toast léger
            //   2. Dernière leçon + quiz dispo + pas encore validé → bascule
            //      immédiate ET VISIBLE vers le quiz (switchTab + scroll + toast
            //      explicite) ; on quitte le mode présentation pour que l'user
            //      voie tout de suite l'interface du quiz.
            //   3. Dernière leçon + pas de quiz → toast "Module terminé" et on
            //      revient sur l'overview du module (sortie du mode présentation).
            const isLastLesson = activeLessonIdx >= lessons.length - 1;
            const newQuizPassed = (r.data.progress?.best_score ?? -1) >= 70;
            if (!isLastLesson) {
                setActiveLessonIdx(activeLessonIdx + 1);
                toast.success("Leçon validée — leçon suivante !");
            } else if (quiz) {
                // Iter-27.95 — On bascule TOUJOURS vers le quiz sur la dernière
                // leçon si un quiz existe, même si l'user l'a déjà réussi. Le
                // bouton "Passer au quiz" doit toujours respecter l'intention
                // utilisateur. Si déjà validé, le toast l'indique avec nuance.
                setPresentationMode(false);
                switchTab("quiz");
                if (newQuizPassed) {
                    toast.success("Module terminé ! Tu peux retenter le quiz pour booster ton score.", {
                        duration: 4500,
                    });
                } else {
                    toast.success("Module terminé ! Place au quiz pour valider.", {
                        duration: 4500,
                    });
                }
                setTimeout(() => {
                    quizSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }, 350);
            } else {
                // Module sans quiz (cas rare) : on sort de la présentation.
                setPresentationMode(false);
                toast.success("Module terminé !", { duration: 4500 });
            }
        } catch (e) {
            // Iter-27.28 — Anti-farming: handle 400 with wait_seconds_remaining
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.wait_seconds_remaining) {
                setWaitRemaining(detail.wait_seconds_remaining);
                setMinSeconds(detail.min_seconds || minSeconds);
                toast.error(detail.message || `Reste ${detail.wait_seconds_remaining}s avant de pouvoir valider.`);
            } else {
                toast.error(typeof detail === "string" ? detail : "Erreur");
            }
        }
    };

    const submitQuiz = async (answers) => {
        try {
            // Iter-27.17: pass served_indexes back to the server when in pool_mode
            const payload = {
                module_id: id,
                answers,
            };
            if (quiz?.pool_mode && quiz?.served_indexes) {
                payload.served_indexes = quiz.served_indexes;
            }
            const { data: result } = await api.post("/quizzes/submit", payload);
            setQuizResult(result);
            const r = await api.get(`/modules/${id}`);
            setData(r.data);
            if (result.passed) {
                let msg = `Validé ! ${result.score}%`;
                if (result.points_earned > 0) {
                    msg += ` • +${result.points_earned} pts`;
                } else if (result.award_reason === "cooldown_active") {
                    msg += " • Pas de point cette fois (déjà validé dans les 24h)";
                } else if (result.award_reason === "max_passes_reached") {
                    msg += " • Pas de point (déjà maîtrisé)";
                }
                // Iter-30.57 — Toast persistant avec action "Module suivant" /
                // "Voir mon parcours" pour que le user n'ait même pas besoin
                // de scroller pour comprendre la suite logique.
                if (onboardingMode) {
                    toast.success(msg);
                } else if (nextModule) {
                    toast.success(msg, {
                        description: `Prochaine étape : ${nextModule.title}`,
                        duration: 9000,
                        action: {
                            label: "Module suivant",
                            onClick: () => {
                                navigate(`/module/${nextModule.module_id}?present=1`);
                            },
                        },
                    });
                } else {
                    toast.success(msg, {
                        description: "🎉 Tu as terminé tous les modules de ton parcours !",
                        duration: 9000,
                        action: {
                            label: "Voir mon parcours",
                            onClick: () => navigate("/parcours"),
                        },
                    });
                }
                if (result.points_earned > 0) {
                    window.dispatchEvent(new CustomEvent("shariti:points-changed"));
                }
            } else {
                toast.error(`${result.score}% — il faut 70% minimum`);
            }
        } catch (e) {
            toast.error("Erreur soumission quiz");
        }
    };

    // Sync tab state with URL query (used by switchTab — keeps deep links shareable)

    const sendComment = async () => {
        if (!newComment.trim()) return;
        try {
            const { data: c } = await api.post("/comments", {
                module_id: id,
                content: newComment,
            });
            setComments([c, ...comments]);
            setNewComment("");
            toast.success("Commentaire publié");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Erreur d'envoi — vérifie ta connexion et réessaie.",
            );
        }
    };

    // Total comments including replies for the tab label
    const totalComments = comments.reduce(
        (n, c) => n + 1 + ((c.replies && c.replies.length) || 0),
        0,
    );

    return (
        <Layout
            title={mod.title}
            subtitle={`${LEVEL_FR[mod.level] || "Débutant"} · ${mod.duration_minutes} min`}
            action={
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => podcast.start(id)}
                        disabled={podcast.isActive && podcast.moduleId === id}
                        data-testid="module-podcast-launch"
                        className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full bg-shariti-orange/12 border border-shariti-orange/30 text-shariti-orange-light hover:bg-shariti-orange/20 hover:text-white text-[11px] sm:text-xs font-bold transition-all disabled:opacity-50"
                        title="Écouter ce module en continu (idéal trajet)"
                        aria-label="Écouter ce module en mode podcast"
                    >
                        <Headphones className="w-3.5 h-3.5 shrink-0" />
                        <span className="sm:hidden">
                            {podcast.isActive && podcast.moduleId === id ? "…" : "Podcast"}
                        </span>
                        <span className="hidden sm:inline">
                            {podcast.isActive && podcast.moduleId === id ? "En cours..." : "Mode podcast"}
                        </span>
                    </button>
                    <Link
                        to="/parcours"
                        className="text-xs font-bold text-shariti-orange-light hidden sm:inline-flex items-center gap-1"
                        data-testid="back-to-parcours"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Parcours
                    </Link>
                </div>
            }
        >
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
                {/* Video player */}
                <div className="col-span-12 lg:col-span-8 space-y-4">
                    <div ref={lessonTopRef} aria-hidden="true" />
                    {(() => {
                        // Iter-27.38 — Choix vidéo vs présentation au niveau leçon.
                        // Le backend nous donne déjà `effective_video_url` (placeholder filtré)
                        // et `effective_display_mode` (auto/video/presentation résolu).
                        const effVideo = activeLesson?.effective_video_url ?? activeLesson?.video_url ?? "";
                        const effMode = activeLesson?.effective_display_mode || (effVideo ? "video" : (activeLesson?.rich_content ? "presentation" : "video"));
                        const showVideoSlot = effMode === "video" || (effMode !== "presentation" && !activeLesson?.rich_content);
                        if (!showVideoSlot) return null;
                        return (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="aspect-video rounded-3xl overflow-hidden glass-card relative"
                                data-testid="video-player"
                            >
                                {effVideo ? (
                                    <iframe
                                        src={getEmbedUrl(effVideo)}
                                        title={activeLesson.title}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="w-full h-full"
                                    />
                                ) : (
                                    <div
                                        className="absolute inset-0 flex flex-col items-center justify-center text-center p-6"
                                        style={{
                                            backgroundImage: mod.cover_image
                                                ? `linear-gradient(135deg, rgba(238,114,46,0.55), rgba(20,20,20,0.92)), url(${mod.cover_image})`
                                                : "linear-gradient(135deg, rgba(238,114,46,0.35), rgba(20,20,20,0.92))",
                                            backgroundSize: "cover",
                                            backgroundPosition: "center",
                                        }}
                                        data-testid="lesson-fallback-image"
                                    >
                                        <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 text-shariti-orange mb-3 drop-shadow-[0_4px_18px_rgba(238,114,46,0.55)]" />
                                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2">
                                            {activeLesson?.rich_content ? "Mode présentation premium" : "Leçon écrite premium"}
                                        </p>
                                        <p className="text-xl sm:text-2xl font-extrabold text-white tracking-tight max-w-xl leading-tight">
                                            {activeLesson?.title}
                                        </p>
                                        {activeLesson?.duration && (
                                            <p className="text-xs sm:text-sm text-white/70 font-light mt-2">
                                                Lecture estimée · {activeLesson.duration}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </motion.div>
                        );
                    })()}

                    <div className="glass-card rounded-3xl p-6">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-2">
                            Leçon en cours
                        </p>
                        <h2 className="text-2xl font-extrabold text-white mb-2 tracking-tight">
                            {activeLesson?.title}
                        </h2>
                        <p className="text-white/60 font-light text-sm mb-4">
                            {activeLesson?.description}
                        </p>
                        {activeLesson &&
                            !progress?.completed_lessons?.includes(activeLesson.lesson_id) && (
                                <div className="flex flex-col gap-2">
                                    {(() => {
                                        const hasVideo = !!activeLesson.video_url;
                                        const hasRichContent = !!activeLesson.rich_content;
                                        // Iter-27.36 — content-consumed gate:
                                        //   • Presentation mode → all slides viewed at least 3s each
                                        //   • Classic mode (or no rich_content) → scrolled to end sentinel
                                        const contentConsumed = hasRichContent && presentationMode
                                            ? allSlidesViewed
                                            : scrolledToEnd;
                                        const isLocked = waitRemaining > 0 || !contentConsumed;
                                        const labelLocked = waitRemaining > 0
                                            ? `Marquer comme vu — ${waitRemaining}s`
                                            : presentationMode && hasRichContent
                                              ? "Marquer comme vu — termine les slides"
                                              : "Marquer comme vu — fais défiler jusqu'en bas";
                                        return (
                                            <>
                                                <button
                                                    onClick={() => completeLesson(activeLesson.lesson_id)}
                                                    disabled={isLocked}
                                                    data-testid="complete-lesson-button"
                                                    className={`inline-flex items-center gap-2 font-bold py-2.5 px-5 rounded-2xl transition-all w-fit border ${
                                                        isLocked
                                                            ? "bg-shariti-orange/15 text-shariti-orange-light border-shariti-orange/40 cursor-not-allowed"
                                                            : "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light border-shariti-orange"
                                                    }`}
                                                >
                                                    {isLocked ? (
                                                        <>
                                                            <Lock className="w-4 h-4" />
                                                            {labelLocked}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle2 className="w-4 h-4" />
                                                            Marquer comme vu
                                                        </>
                                                    )}
                                                </button>
                                                {isLocked && (
                                                    <p
                                                        className="text-[11px] text-white/55 font-light leading-relaxed"
                                                        data-testid="lesson-wait-hint"
                                                    >
                                                        {!pageVisible && waitRemaining > 0 ? (
                                                            <>⏸ Le compte à rebours est en pause tant que cet onglet n&apos;est pas actif.</>
                                                        ) : waitRemaining > 0 && !contentConsumed ? (
                                                            presentationMode && hasRichContent ? (
                                                                <>Prends {minSeconds}s pour vraiment {hasVideo ? "regarder + parcourir les slides" : "parcourir chaque slide"} — le bouton se débloque dès que tout est consommé.</>
                                                            ) : (
                                                                <>Prends {minSeconds}s pour {hasVideo ? "regarder la vidéo et lire les compléments" : "vraiment lire la leçon"}, et fais défiler jusqu&apos;en bas — le bouton se débloque ensuite.</>
                                                            )
                                                        ) : waitRemaining > 0 ? (
                                                            <>Prends {minSeconds}s pour {hasVideo ? "regarder la vidéo et lire les compléments" : "vraiment lire la leçon"} — c&apos;est ce qui ancre l&apos;apprentissage.</>
                                                        ) : (
                                                            presentationMode && hasRichContent ? (
                                                                <>Bientôt fini — parcours simplement toutes les slides jusqu&apos;à la dernière pour valider.</>
                                                            ) : (
                                                                <>Bientôt fini — fais simplement défiler jusqu&apos;en bas du contenu pour valider.</>
                                                            )
                                                        )}
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                    </div>

                    {/* Iter-27.17: rich_content lesson body (if present)
                        Iter-27.36 — Mode Présentation Premium (par défaut) ou Classique.
                        Iter-27.38 — Si admin force display_mode="video", on cache les slides. */}
                    {activeLesson?.rich_content && activeLesson.effective_display_mode !== "video" && (
                        <div>
                            <div className="flex items-center justify-end mb-3">
                                <div
                                    className="inline-flex p-1 rounded-full bg-white/4 border border-white/10 text-[10px] font-bold tracking-wider"
                                    data-testid="lesson-mode-toggle"
                                >
                                    <button
                                        onClick={() => setPresentationMode(true)}
                                        data-testid="lesson-mode-presentation"
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
                                            presentationMode
                                                ? "bg-shariti-orange text-white"
                                                : "text-white/55 hover:text-white"
                                        }`}
                                    >
                                        <Presentation className="w-3 h-3" />
                                        Présentation
                                    </button>
                                    <button
                                        onClick={() => setPresentationMode(false)}
                                        data-testid="lesson-mode-classic"
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
                                            !presentationMode
                                                ? "bg-white/10 text-white"
                                                : "text-white/55 hover:text-white"
                                        }`}
                                    >
                                        <AlignLeft className="w-3 h-3" />
                                        Classique
                                    </button>
                                </div>
                            </div>
                            {presentationMode ? (
                                <div data-testid="lesson-presentation-container">
                                    <LessonPresentation
                                        key={activeLesson.lesson_id}
                                        rich_content={activeLesson.rich_content}
                                        lessonId={activeLesson.lesson_id}
                                        onAllSlidesViewed={() => setAllSlidesViewed(true)}
                                        onAdvanceLesson={() => completeLesson(activeLesson.lesson_id)}
                                        isLastLessonOfModule={activeLessonIdx >= lessons.length - 1}
                                        hasQuiz={Boolean(quiz)}
                                    />
                                </div>
                            ) : (
                                <div data-testid="rich-lesson-container">
                                    {/* Iter-30.31 — En mode Classique sur mobile, on
                                        affiche un bandeau cliquable qui propose
                                        d'écouter la leçon en mode podcast. Sinon
                                        l'apprenant mobile n'a aucun moyen de découvrir
                                        cette fonctionnalité (le bouton header
                                        "Mode podcast" est en haut, facilement raté). */}
                                    <button
                                        onClick={() => podcast.start(id)}
                                        disabled={podcast.isActive && podcast.moduleId === id}
                                        data-testid="lesson-classic-podcast-cta"
                                        className="sm:hidden w-full mb-4 group inline-flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-shariti-orange/15 to-shariti-orange/5 border border-shariti-orange/30 text-left hover:from-shariti-orange/25 hover:border-shariti-orange/50 transition-all disabled:opacity-60"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-shariti-orange/20 border border-shariti-orange/40 flex items-center justify-center shrink-0">
                                                <Headphones className="w-4 h-4 text-shariti-orange-light" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[13px] font-extrabold text-white leading-tight">
                                                    {podcast.isActive && podcast.moduleId === id
                                                        ? "Lecture en cours…"
                                                        : "Écoute cette leçon en mode podcast"}
                                                </p>
                                                <p className="text-[10.5px] text-white/55 leading-tight mt-0.5">
                                                    Idéal en trajet — toutes les leçons du module enchaînées
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-shariti-orange-light shrink-0">
                                            Podcast
                                        </span>
                                    </button>
                                    <RichLesson rich_content={activeLesson.rich_content} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Iter-27.32 — Sentinel for "scrolled to end" detection (anti-farming).
                        When this becomes visible, the user has reached the end of the lesson body.
                        We intentionally place it BEFORE the tabs so users don't have to scroll
                        through the comments section to unlock — that would be punishing. */}
                    <div
                        ref={lessonEndSentinelRef}
                        data-testid="lesson-end-sentinel"
                        className="h-1 w-full"
                        aria-hidden="true"
                    />

                    {/* Tabs */}
                    <div
                        className="flex gap-2 p-1.5 glass-card rounded-full w-fit"
                        data-testid="module-tabs"
                        ref={quizSectionRef}
                    >
                        {[
                            { id: "lessons", label: "Leçons" },
                            { id: "quiz", label: "Quiz" },
                            { id: "comments", label: `Commentaires (${totalComments})` },
                        ].map((t) => (
                            <button
                                key={t.id}
                                onClick={() => switchTab(t.id)}
                                data-testid={`tab-${t.id}`}
                                className={`px-4 py-2 rounded-full text-sm font-bold transition-all relative ${
                                    tab === t.id
                                        ? "bg-shariti-orange text-white"
                                        : "text-white/55"
                                }`}
                            >
                                {t.label}
                                {t.id === "quiz" &&
                                    allLessonsDone &&
                                    !quizPassed &&
                                    tab !== "quiz" && (
                                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-shariti-orange-light animate-pulse ring-2 ring-shariti-deep-black" />
                                    )}
                            </button>
                        ))}
                    </div>

                    {/* Intuitive CTA banner — only when all lessons done but quiz not yet passed */}
                    {allLessonsDone && !quizPassed && quiz && tab !== "quiz" && (
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => switchTab("quiz")}
                            data-testid="quiz-cta-banner"
                            className="w-full glass-card rounded-3xl p-4 sm:p-5 border border-shariti-orange/40 hover:border-shariti-orange transition-all text-left flex items-center gap-3 sm:gap-4 group"
                        >
                            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-shariti-orange/20 flex items-center justify-center shrink-0">
                                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-shariti-orange" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-0.5">
                                    Plus qu&apos;une étape
                                </p>
                                <p className="font-extrabold text-white text-sm sm:text-base leading-tight">
                                    Toutes les leçons sont validées — passe au quiz (70 % min) pour finaliser ce module.
                                </p>
                            </div>
                            <ArrowRight className="w-5 h-5 text-shariti-orange shrink-0 group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                    )}

                    <AnimatePresence mode="wait">
                        {tab === "lessons" && (
                            <motion.div
                                key="lessons"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="space-y-2"
                            >
                                {lessons.map((l, i) => {
                                    const done = progress?.completed_lessons?.includes(l.lesson_id);
                                    return (
                                        <button
                                            key={l.lesson_id}
                                            onClick={() => setActiveLessonIdx(i)}
                                            data-testid={`lesson-item-${i}`}
                                            className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl transition-all ${
                                                activeLessonIdx === i
                                                    ? "bg-shariti-orange/15 border border-shariti-orange/40"
                                                    : "glass-card hover:border-shariti-orange/20"
                                            }`}
                                        >
                                            <div
                                                className={`w-9 h-9 rounded-xl flex items-center justify-center font-extrabold ${
                                                    done
                                                        ? "bg-shariti-orange text-white"
                                                        : "bg-white/5 text-white/60"
                                                }`}
                                            >
                                                {done ? (
                                                    <CheckCircle2 className="w-5 h-5" />
                                                ) : (
                                                    <Play className="w-4 h-4 fill-current" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-white text-sm">
                                                    {l.title}
                                                </p>
                                                <p className="text-xs text-white/40">
                                                    {l.duration}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </motion.div>
                        )}

                        {tab === "quiz" && (
                            <motion.div
                                key="quiz"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                            >
                                {quiz ? (
                                    <Quiz
                                        quiz={quiz}
                                        onSubmit={submitQuiz}
                                        lastResult={quizResult}
                                        onboardingMode={onboardingMode}
                                        nextActionSlot={
                                            quizPassed ? (
                                                nextModule ? (
                                                    <motion.div
                                                        ref={nextModuleRef}
                                                        initial={{ opacity: 0, y: 8 }}
                                                        animate={{
                                                            opacity: 1,
                                                            y: 0,
                                                            boxShadow: [
                                                                "0 0 0 0 rgba(238,114,46,0)",
                                                                "0 0 0 14px rgba(238,114,46,0.22)",
                                                                "0 0 0 0 rgba(238,114,46,0)",
                                                            ],
                                                        }}
                                                        transition={{
                                                            duration: 0.4,
                                                            boxShadow: { duration: 2.2, repeat: 3, repeatDelay: 0.4 },
                                                        }}
                                                        className="rounded-3xl"
                                                    >
                                                        <Link
                                                            to={`/module/${nextModule.module_id}?present=1`}
                                                            data-testid="next-module-cta-top"
                                                            className="block glass-card rounded-3xl p-4 sm:p-5 border-2 border-shariti-orange/60 hover:border-shariti-orange transition-all group"
                                                        >
                                                            <div className="flex items-center gap-3 sm:gap-4">
                                                                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-shariti-orange/25 flex items-center justify-center shrink-0">
                                                                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-shariti-orange group-hover:translate-x-0.5 transition-transform" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-0.5">
                                                                        Étape suivante débloquée
                                                                    </p>
                                                                    <p className="font-extrabold text-white text-sm sm:text-base leading-tight truncate">
                                                                        Continuer avec : {nextModule.title}
                                                                    </p>
                                                                    <p className="text-[11px] text-white/55 mt-0.5">
                                                                        {nextModule.duration_minutes
                                                                            ? `${nextModule.duration_minutes} min`
                                                                            : ""}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </motion.div>
                                                ) : (
                                                    <motion.div
                                                        ref={nextModuleRef}
                                                        initial={{ opacity: 0, y: 8 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        className="glass-card rounded-3xl p-5 text-center border-2 border-shariti-orange/40"
                                                    >
                                                        <p className="text-shariti-orange-light font-extrabold text-sm mb-3">
                                                            🎉 Bravo, tu as terminé tous les modules de ton parcours !
                                                        </p>
                                                        <Link
                                                            to="/parcours"
                                                            data-testid="back-to-parcours-after-quiz-top"
                                                            className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-widest px-4 py-2.5 rounded-2xl hover:bg-shariti-orange-light transition-all"
                                                        >
                                                            <ArrowRight className="w-4 h-4" />
                                                            Voir mon parcours
                                                        </Link>
                                                    </motion.div>
                                                )
                                            ) : null
                                        }
                                    />
                                ) : (
                                    <p className="text-white/50">Pas de quiz pour ce module.</p>
                                )}
                                {quizResult && (
                                    <button
                                        onClick={() => setQuizResult(null)}
                                        className="mt-4 w-full glass-card rounded-2xl py-3 font-bold text-white hover:border-shariti-orange/40"
                                    >
                                        Repasser le quiz
                                    </button>
                                )}
                                {/* Iter-30.57 — Les anciens CTA en bas (Iter-27.86 / 30.44)
                                    ont été déplacés EN HAUT (via nextActionSlot du Quiz)
                                    pour être visibles immédiatement sans scroller la
                                    liste de feedback. Évite la duplication ici. */}
                            </motion.div>
                        )}

                        {tab === "comments" && (
                            <motion.div
                                key="comments"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="space-y-3"
                            >
                                <div className="glass-card rounded-2xl p-3 flex gap-2 items-end">
                                    <textarea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Partage ta question ou ton retour terrain..."
                                        rows={2}
                                        data-testid="comment-textarea"
                                        className="flex-1 bg-transparent resize-none outline-none text-white placeholder-white/30 text-sm"
                                    />
                                    <button
                                        onClick={sendComment}
                                        data-testid="comment-submit-button"
                                        className="w-10 h-10 rounded-xl bg-shariti-orange flex items-center justify-center hover:bg-shariti-orange-light transition-all"
                                    >
                                        <Send className="w-4 h-4 text-white" />
                                    </button>
                                </div>
                                <CommentThread
                                    comments={comments}
                                    moduleId={id}
                                    onChange={(updater) => setComments((prev) => updater(prev))}
                                />
                                {comments.length === 0 && (
                                    <p className="text-sm text-white/40 text-center py-8">
                                        Sois le premier à partager ton expérience 💬
                                    </p>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Sidebar progress */}
                <div className="col-span-12 lg:col-span-4 space-y-4">
                    <div className="glass-card rounded-3xl p-6">
                        <p className="text-xs font-thin tracking-[0.25em] uppercase text-shariti-orange-light mb-3">
                            Ta progression
                        </p>
                        <p className="text-5xl font-extrabold text-white mb-1">
                            {progress?.progress_percent || 0}%
                        </p>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-4 mb-3">
                            <div
                                className="h-full bg-shariti-orange rounded-full"
                                style={{ width: `${progress?.progress_percent || 0}%` }}
                            />
                        </div>
                        <p className="text-xs text-white/50">
                            {progress?.completed_lessons?.length || 0} / {lessons.length} leçons
                        </p>
                        {progress?.best_score != null && (
                            <div className="mt-4 pt-4 border-t border-white/5">
                                <p className="text-xs text-white/50 mb-1">Meilleur quiz</p>
                                <p className="text-2xl font-extrabold text-shariti-orange-light">
                                    {progress.best_score}%
                                </p>
                            </div>
                        )}
                    </div>

                    {mod.cover_image && (
                        <div className="rounded-3xl overflow-hidden aspect-square relative glass-card">
                            <img
                                src={mod.cover_image}
                                alt={mod.title}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-shariti-black via-transparent to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4">
                                <p className="text-xs font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                    Niveau
                                </p>
                                <p className="text-xl font-extrabold text-white">
                                    {LEVEL_FR[mod.level] || "Débutant"}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
